import React, { useCallback, useEffect } from 'react';

import { PreviewPanelsProvider } from '@atlassian/preview-panels-api';
import { SmartCardProvider, useSmartLinkContext } from '@atlaskit/link-provider';

import { useClosePreviewPanelOnUrlChange } from './useClosePreviewPanelOnUrlChange';
import { useOpenPreviewPanel } from './useOpenPreviewPanel';
import { useClosePreviewPanel } from './useClosePreviewPanel';
import { useOpenPreviewModal } from './useOpenPreviewModal';

interface ConfluencePreviewPanelsProviderProps {
	children: React.ReactNode;
}

// Component that uses the URL change detection hooks inside the provider context
const UrlChangeDetector = () => {
	useClosePreviewPanelOnUrlChange();
	return null; // This component doesn't render anything
};

const PostMessageListener = () => {
	const { open } = useOpenPreviewPanel();
	const [currentUrl, setCurrentUrl] = React.useState('');
	const { actions, openModal } = useOpenPreviewModal(currentUrl);

	// Listen for messages from iframes to open preview panels
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			const { payload } = event.data;

			if (event.data?.type === 'OPEN_EMBED_MODAL' && payload?.modal) {
				if (!actions || actions.length === 0) {
					setCurrentUrl(payload.modal.url);
					window.parent.postMessage(
						{
							type: 'OPEN_EMBED_MODAL',
							payload: payload,
						},
						'*',
					);
					return;
				}
				openModal();
				return;
			}

			// Only handle messages with the expected type
			if (event.data?.type !== 'OPEN_PREVIEW_PANEL') {
				return;
			}

			if (payload?.url) {
				open(payload);
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [open, actions, openModal]);

	return null;
};

export const ConfluencePreviewPanelsProvider = ({
	children,
}: ConfluencePreviewPanelsProviderProps) => {
	const { closeAllPanels, closeOnlyPreviewPanel } = useClosePreviewPanel();

	let renderers;
	try {
		const parentContext = useSmartLinkContext();
		renderers = parentContext?.renderers;
	} catch {
		renderers = undefined;
	}

	const onPreviewPanelOpen = useCallback(() => {
		/*
		Any time a Preview Panel is opened in Confluence, we want to close all
		non-preview panel side panels (like the object sidebar) to ensure no panel conflicts.
		*/
		closeAllPanels();
	}, [closeAllPanels]);

	const onPreviewPanelClose = useCallback(() => {
		// Add any additional logic here for closing preview panels.
		/*
			Any time a Preview Panel is closed in Confluence, if the type of the previous panel and the
			next panel are different, we want to close the previous panel before opening the new one. In order to
			do this we need to pass the preview panel api
		*/
		closeOnlyPreviewPanel();
	}, [closeOnlyPreviewPanel]);

	return (
		<SmartCardProvider renderers={renderers}>
			<PreviewPanelsProvider
				onPreviewPanelOpen={onPreviewPanelOpen}
				onPreviewPanelClose={onPreviewPanelClose}
			>
				{/* URL change detection hooks need to be inside the provider to access context */}
				<UrlChangeDetector />
				<PostMessageListener />
				{children}
			</PreviewPanelsProvider>
		</SmartCardProvider>
	);
};
